import { Link, useOutletContext } from 'react-router-dom';
import { riskFollowUps } from '@/domain/workflow/brief';
import { goalStatusLabel } from '@/domain/workflow/documents';
import { sessionStage } from '@/domain/workflow/home';
import { currentStepLabel } from '@/domain/workflow/approaches';
import { useLoad } from '../../hooks/useLoad';
import { useService } from '../../hooks/useService';
import type { ClientOutlet } from './ClientShell';
import { CreatedAt } from '../../components/CreatedAt';
import { ClaudeProjectPanel } from '../../components/ClaudeProjectPanel';

/** 내담자 개요 (UX_FLOW §6) */
export function ClientOverview() {
  const { client } = useOutletContext<ClientOutlet>();
  const svc = useService();
  const { data } = useLoad(async () => {
    const [goals, formulation, sessions] = await Promise.all([
      svc.listGoals(client.id),
      svc.currentFormulation(client.id),
      svc.listSessions(client.id),
    ]);
    const last = sessions.filter((s) => s.status === 'approved').at(-1);
    const [analysis, risk] = last ? await Promise.all([svc.getAnalysis(last.id), svc.getRisk(last.id)]) : [null, null];
    return { goals, formulation, sessions, analysis, risk };
  }, [svc, client.id]);

  if (!data) return <p className="muted">불러오는 중…</p>;
  const active = data.goals.filter((g) => !['achieved', 'terminated'].includes(g.status));
  const hypotheses = (data.formulation?.beliefHypotheses ?? []).filter((h) => h.status === 'hypothesis').slice(0, 4);
  const risks = riskFollowUps(data.risk ?? undefined);

  return (
    <div className="stack">
      <div className="stat-row">
        <Link className="stat-card" to="treatment">
          <span className="muted small">치료 방향</span>
          <strong>{currentStepLabel(client.treatmentPlan) ?? '정하기 전'}</strong>
        </Link>
        <Link className="stat-card" to="goals">
          <span className="muted small">상담목표</span>
          <strong>{active.length ? `${active.length}개 진행 중` : '없음 · 추가하기'}</strong>
        </Link>
      </div>
      <ClaudeProjectPanel client={client} />
      {risks.length > 0 && (
        <div className="card warn-card">
          <h2>위험/주의</h2>
          <ul>
            {risks.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        </div>
      )}
      <div className="card">
        <h2>현재 상담목표</h2>
        {active.length === 0 ? (
          <p className="muted">
            목표가 없습니다. <Link to="goals">목표 추가</Link>
          </p>
        ) : (
          <ul>
            {active.slice(0, 3).map((g) => (
              <li key={g.id}>
                {g.title} <span className="badge">{goalStatusLabel(g.status)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="card">
        <h2>현재 사례개념화</h2>
        {data.formulation ? (
          <>
            <p>
              v{data.formulation.version} · {data.formulation.currentProblems?.join(', ') || '주요 문제 미기재'}
            </p>
            <Link to="formulation">상세 보기</Link>
          </>
        ) : (
          <p className="muted">
            아직 없습니다. <Link to="formulation">작성하기</Link>
          </p>
        )}
      </div>
      {hypotheses.length > 0 && (
        <div className="card">
          <h2>확인 중인 가설</h2>
          <ul>
            {hypotheses.map((h) => (
              <li key={h.id}>
                <span className="badge badge-review">가설</span> {h.text}
              </li>
            ))}
          </ul>
        </div>
      )}
      {data.analysis?.notableChanges?.length ? (
        <div className="card">
          <h2>최근 변화</h2>
          <ul>
            {data.analysis.notableChanges.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </div>
      ) : null}
      <div className="card">
        <h2>회기</h2>
        {data.sessions.length === 0 && <p className="muted">아직 회기가 없습니다.</p>}
        <ul className="list">
          {[...data.sessions].reverse().map((s) => (
            <li key={s.id} className="list-row">
              <span>
                {s.sessionNumber}회기 · {s.sessionDate}
                {s.durationMinutes ? ` · ${s.durationMinutes}분` : ''}
                <br />
                <CreatedAt iso={s.createdAt} />
              </span>
              <Link to={sessionStage(s).path}>{s.status === 'approved' ? '보기' : sessionStage(s).stage}</Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
